import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiClient } from '@/api/client';
import type { components } from '@/api/generated/schema';

export type Job = components['schemas']['JobResponse'];
export type JobListFilterPreferences = components['schemas']['JobListFilterPreferences'];

export { useTechnicians } from '@/routes/dispatch/useDispatchBoard';

/** The list screen's own filter/sort/page state — what the URL and the saved preferences both hold. */
export interface JobListFilters {
  status: string | null;
  assignedUserId: string | null;
  unassignedOnly: boolean;
  jobType: string;
  scheduledFrom: string | null;
  scheduledTo: string | null;
  includeUnscheduled: boolean;
  customerId: string | null;
  sortBy: string;
  sortDirection: 'asc' | 'desc';
  page: number;
  pageSize: number;
}

export const DEFAULT_SORT_BY = 'scheduledStart';
export const DEFAULT_SORT_DIRECTION: JobListFilters['sortDirection'] = 'asc';
export const DEFAULT_PAGE_SIZE = 25;

/**
 * The job list, filtered, sorted, and paged exactly as `GET /jobs` was asked.
 *
 * `enabled` exists for the date range specifically: a `scheduledFrom`/`To`
 * filter needs the account's timezone to turn a calendar date into the
 * instant the API filters on (`toApiFilters`), and running the query one
 * render before that is known would silently ask for the unfiltered list
 * instead of waiting for the real one.
 */
export function useJobsList(filters: JobListFilters, enabled = true) {
  return useQuery({
    queryKey: ['jobs', 'list', filters],
    enabled,
    queryFn: async () => {
      const { data, response } = await apiClient.GET('/api/v1/jobs', {
        params: {
          query: {
            status: (filters.status as components['schemas']['JobStatus']) ?? undefined,
            assignedUserId: filters.unassignedOnly ? undefined : (filters.assignedUserId ?? undefined),
            unassignedOnly: filters.unassignedOnly || undefined,
            jobType: filters.jobType.trim() || undefined,
            scheduledFrom: filters.scheduledFrom ?? undefined,
            scheduledTo: filters.scheduledTo ?? undefined,
            includeUnscheduled: filters.includeUnscheduled || undefined,
            customerId: filters.customerId ?? undefined,
            sortBy: filters.sortBy,
            sortDirection: filters.sortDirection,
            page: filters.page,
            pageSize: filters.pageSize,
          },
        },
      });
      if (!response.ok || !data) {
        throw new Error(`GET /api/v1/jobs failed: ${response.status}`);
      }
      return data;
    },
    // The current page stays on screen while the next one loads, rather than
    // flashing to a loading state on every click of Next — a page turn is a
    // small, frequent act, and blanking the table for it reads as a bigger
    // change than it is.
    placeholderData: keepPreviousData,
  });
}

const jobListFilterPreferencesQueryKey = ['me', 'preferences', 'jobListFilters'] as const;

/**
 * The signed-in user's remembered job list filters (M1-15's "saved per user"
 * AC). Read once to seed a fresh visit to the screen — see `useJobListFilters`
 * — not re-read afterward, so the two never fight over what the URL says
 * while the user is actively changing filters.
 */
export function useJobListFilterPreferences() {
  return useQuery({
    queryKey: jobListFilterPreferencesQueryKey,
    queryFn: async () => {
      const { data, response } = await apiClient.GET('/api/v1/me/preferences/job-list-filters');
      if (!response.ok || !data) {
        throw new Error(`GET /api/v1/me/preferences/job-list-filters failed: ${response.status}`);
      }
      return data;
    },
    staleTime: Infinity,
  });
}

export function useSaveJobListFilterPreferences() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (preferences: JobListFilterPreferences) => {
      const { data, response } = await apiClient.PUT('/api/v1/me/preferences/job-list-filters', {
        body: preferences,
      });
      if (!response.ok || !data) {
        throw new Error(`PUT /api/v1/me/preferences/job-list-filters failed: ${response.status}`);
      }
      return data;
    },
    onSuccess: (data) => queryClient.setQueryData(jobListFilterPreferencesQueryKey, data),
  });
}
